import { ProLayout as AntProLayout } from '@ant-design/pro-components';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { LogoutOutlined, UserOutlined, SettingOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/use-auth';
import { Avatar, Dropdown } from 'antd';
import { menuRoute, filterMenuByPerms } from './menu-config';
import { useApiQuery } from '../api/use-api';
import Logo from './Logo';
import { BRAND_PRIMARY, BRAND_PRIMARY_SOFT, HAIRLINE } from '../theme';

export default function ProLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { account, logout } = useAuth();

  const initial = (account?.name ?? 'A').slice(0, 1).toUpperCase();

  // #225：按有效权限隐藏无权访问的栏目。self 未返回前按 admin 处理（不闪menu），
  // 因为无权账号访问具体页面仍会被后端 RBAC 拦住，菜单只是可用性优化。
  const selfQuery = useApiQuery<{ permissions?: string[]; isAdmin?: boolean }>(
    ['admin-self-perms'],
    async () => {
      const r = await fetch('/api/admin/self', {
        headers: { authorization: `Bearer ${localStorage.getItem('admin_access_token') ?? ''}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<{ permissions?: string[]; isAdmin?: boolean }>;
    },
    { staleTime: 5 * 60 * 1000, retry: false },
  );
  const visibleMenu = selfQuery.data
    ? filterMenuByPerms(menuRoute, selfQuery.data.permissions ?? [], selfQuery.data.isAdmin ?? false)
    : menuRoute;

  return (
    <AntProLayout
      title="AlphaStep"
      logo={<Logo size={22} />}
      layout="mix"
      splitMenus={false}
      siderWidth={216}
      fixSiderbar
      fixedHeader
      contentStyle={{ padding: '16px 24px 24px' }}
      route={visibleMenu}
      location={loc}
      onMenuHeaderClick={() => nav('/dashboard/overview')}
      menuItemRender={(item, dom) => (
        <Link to={item.path!} style={{ display: 'block' }}>
          {dom}
        </Link>
      )}
      breadcrumbRender={(routers = []) =>
        routers.map((r) => ({
          ...r,
          title: r.path ? <Link to={r.path}>{r.breadcrumbName}</Link> : r.breadcrumbName,
        }))
      }
      token={{
        sider: {
          colorMenuBackground: '#FFFFFF',
          colorBgMenuItemSelected: BRAND_PRIMARY_SOFT,
          colorTextMenuSelected: BRAND_PRIMARY,
          colorTextMenuActive: BRAND_PRIMARY,
          colorTextMenu: '#475569',
          colorTextMenuTitle: '#0F172A',
          colorTextMenuItemHover: BRAND_PRIMARY,
          colorBgMenuItemHover: 'rgba(0, 185, 107, 0.06)',
        },
        header: {
          colorBgHeader: '#FFFFFF',
          colorHeaderTitle: '#0F172A',
          heightLayoutHeader: 56,
        },
        bgLayout: '#F6F8FA',
      }}
      avatarProps={{
        size: 'small',
        title: account?.name ?? 'admin',
        render: (_props, _dom) => (
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'me',
                  icon: <UserOutlined />,
                  label: '我的资料',
                  onClick: () => nav('/system/me'),
                },
                {
                  key: 'settings',
                  icon: <SettingOutlined />,
                  label: '系统设置',
                  onClick: () => nav('/stores/list'),
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  danger: true,
                  onClick: () => {
                    logout();
                    nav('/login');
                  },
                },
              ],
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 8,
                transition: 'background 120ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F5F9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Avatar
                size={28}
                style={{
                  background: BRAND_PRIMARY_SOFT,
                  color: BRAND_PRIMARY,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {initial}
              </Avatar>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                {account?.name ?? 'admin'}
              </span>
            </span>
          </Dropdown>
        ),
      }}
      footerRender={() => (
        <div
          style={{
            textAlign: 'center',
            padding: '12px 0',
            fontSize: 12,
            color: '#94A3B8',
            borderTop: `1px solid ${HAIRLINE}`,
            background: '#FFFFFF',
          }}
        >
          AlphaStep · 管理后台 © {new Date().getFullYear()}
        </div>
      )}
    >
      <Outlet />
    </AntProLayout>
  );
}
